import {
  AnyFilterItem,
  FeedState,
  isPeriodItem,
  isValueItem,
} from './feed.reducer';
import { Dashboard, ExternalService, User } from '../models/core.models';
import {
  NomObjectClass,
  NomObjectLifecycleStatus,
  NomObjectTypes,
  NomTimezones,
} from '../constants';
import { ObjectContextSettings, Principal } from '../models/object.models';
import { createSelector } from '@ngrx/store';

import { AppState } from '../../reducers';
import { CoreState } from './core.reducer';
import { FeedSettings } from 'src/app/api/services/feed-api.service';
import { HomeState } from './home.reducer';
import { MainMenuState } from './main-menu.reducer';
import { ObjectState } from './object.reducer';
import { PrincipalState } from './principals.reducer';
import { UserAccessLevel } from '../services/user.service';
import { UserState } from '../reducers/auth.reducer';
import { WidgetState } from './widget.reducer';
import { WorkspaceState } from './workspace.reducer';
import { filterMenuByAccessLevel } from 'src/app/core/models/main-menu.models';

const selectHomeState = (state: AppState) => state.home;

const selectFormState = (state: AppState) => state.form;

export const selectHomeFilterString = createSelector(
  selectHomeState,
  (state: HomeState) => state.filterString,
);

export const selectHomeTab = createSelector(
  selectHomeState,
  (state: HomeState) => state.tab,
);

export const selectHomeSortingField = createSelector(
  selectHomeState,
  (state: HomeState) => state.sortField,
);

export const selectHomeSortingDirection = createSelector(
  selectHomeState,
  (state: HomeState) => state.sortDirection,
);

export const selectHomeContextSettings = createSelector(
  selectHomeState,
  (state: HomeState) => {
    return {
      contextKey: 'object_manager_home',
      contextValue: {
        filterString: state.filterString,
        sortingField: state.sortField,
        selectedOnly: false,
        showFilterTool: false,
        viewFields: [],
        filterFields: [],
        sortingDirection: state.sortDirection,
      },
      lifeCycleStatus: [NomObjectLifecycleStatus.ACTIVE],
    } as ObjectContextSettings;
  },
);

const selectUserState = (state: AppState) => state.user;

export const selectAuth = createSelector(
  selectUserState,
  (userState: UserState) => userState,
);

export const selectIsAuthenticated = createSelector(
  selectUserState,
  (userState: UserState) => userState.isAuthenticated,
);

export const selectUser = createSelector(
  selectUserState,
  (userState: UserState) => userState.user,
);

export const selectUserCompanyColorScheme = createSelector(
  selectUser,
  (user: User | null): string[] => user?.company?.color_scheme ?? [],
);

// * Calendar bounds for date range pickers: the company's contracted period,
// * with "now" as the upper bound when the company has no end date.
export const selectCompanyDateBounds = createSelector(
  selectUser,
  (user: User | null): { min: Date | null; max: Date } => ({
    min: user?.company?.start_date
      ? new Date(user.company.start_date * 1000)
      : null,
    max: user?.company?.end_date
      ? new Date(user.company.end_date * 1000)
      : new Date(),
  }),
);

export const selectUserHasMattermostAccess = createSelector(
  selectUserState,
  (userState: UserState): boolean => {
    const user = userState.user;
    if (!user) {
      return false;
    }

    // make sure we have an array to work with
    const serviceIds: number[] = Array.isArray(user.external_service_ids)
      ? user.external_service_ids
      : [];

    // check for any Mattermost‑related service
    return [
      ExternalService.MATTERMOST_MOE,
      ExternalService.MATTERMOST_TEST, // add any others you care about
      ExternalService.MATTERMOST_MOE_PROD,
    ].some((svc) => serviceIds.includes(svc));
  },
);

export const selectCurrentCompanyId = createSelector(
  selectUserState,
  (userState: UserState) => userState.user.active_company_id,
);

export const selectIsAuthChecked = createSelector(
  selectUserState,
  (userState: UserState) => userState.isAuthChecked,
);

export const selectAccessToken = (s: UserState) => s.accessToken;
export const selectRefreshToken = (s: UserState) => s.refreshToken;

export const selectIsAccessTokenExpired = (s: UserState) =>
  !s.accessTokenExpiresAt || s.accessTokenExpiresAt * 1000 <= Date.now();

export const selectIsRefreshTokenExpired = (s: UserState) =>
  !s.refreshTokenExpiresAt || s.refreshTokenExpiresAt * 1000 <= Date.now();

export const selectIsAuthLoading = createSelector(
  selectUserState,
  (userState: UserState) => userState.isAuthLoading,
);

export const selectRedirectUrl = createSelector(
  selectUserState,
  (userState: UserState) => userState.redurectUrl,
);

export const selectIsAuthError = createSelector(
  selectUserState,
  (userState: UserState) => userState.isAuthError,
);

export const selectAuthError = createSelector(
  selectUserState,
  (userState: UserState) => userState.authError,
);

export const selectUserCompany = createSelector(
  selectUserState,
  (userState: UserState) => userState?.user?.company,
);

export const selectDefaultTimezoneId = createSelector(
  selectUserState,
  (userState: UserState) =>
    userState.user?.timezone?.id ??
    userState.user?.company?.timezone?.id ??
    NomTimezones.EUROPE_SOFIA,
);

// Base Selector for CoreState

const selectMainMenuState = (state: AppState) => state.mainMenu;

export const selectMainMenuCanToggle = createSelector(
  selectMainMenuState,
  (state: MainMenuState) => state.canToggleMenu,
);

const selectCoreState = (state: AppState) => state.core;

const selectPrincipalsState = (state: AppState) => state.principals;

// Simple selectors
export const selectHomeObjectsLifeCycle = createSelector(
  selectCoreState,
  (state: CoreState) => state.homeObjectsLifeCycle,
);

export const selectWorspaceObjectsLifeCycle = createSelector(
  selectCoreState,
  (state: CoreState) => state.workspaceObjectsLifeCycle,
);

export const selectCoreAppSection = createSelector(
  selectCoreState,
  (state: CoreState) => state.appSection,
);

export const selectCoreAvailableModules = createSelector(
  selectCoreState,
  (state: CoreState) => state.modules,
);

export const selectCoreActiveModule = createSelector(
  selectCoreState,
  (state: CoreState) => state.modules.find((module) => module.active),
);

export const selectCoreLanguage = createSelector(
  selectCoreState,
  (state: CoreState) => state.language,
);

export const selectError = createSelector(
  selectCoreState,
  (state: CoreState) => state.error,
);

export const selectCoreDarkMode = createSelector(
  selectCoreState,
  (state: CoreState) => state.darkMode,
);

export const selectCreatingFeed = createSelector(
  selectCoreState,
  (state: CoreState) => state.creatingFeed,
);

export const selectWorkspaceState = (state: AppState) => state.workspace;

export const selectWorkspaceFilterString = createSelector(
  selectWorkspaceState,
  (state: WorkspaceState) => state.filterString,
);

export const selectWorkspaceSortingField = createSelector(
  selectWorkspaceState,
  (state: WorkspaceState) => state.sortField,
);

export const selectWorkspaceSortingDirection = createSelector(
  selectWorkspaceState,
  (state: WorkspaceState) => state.sortDirection,
);

export const selectWorkspaceTab = createSelector(
  selectWorkspaceState,
  (state: WorkspaceState) => state.tab,
);

export const selectWorkspaceCurrentWorkspaceId = createSelector(
  selectWorkspaceState,
  (state: WorkspaceState) => state.currentWorkspaceId,
);

const selectFeedState = (state: AppState) => state.feed;
const selectWidgetState = (state: AppState) => state.widget;
const selectObjectState = (state: AppState) => state.object;

export const selectFeedTimeShown = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.timeShown,
);

export const selectCountSelectedWidgets = createSelector(
  selectWidgetState,
  (widgetState: WidgetState) => widgetState.countSelectedWidgets,
);

export const selectWidgetSearchString = createSelector(
  selectWidgetState,
  (widgetState: WidgetState) => widgetState.searchString,
);

export const selectWidgetsLanguageId = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.widgetsLanguageId,
);

export const selectWidgetViewConfig = createSelector(
  selectWidgetState,
  (widgetState: WidgetState) => widgetState.viewConfig,
);

export const selectSelectedPreviewWidget = createSelector(
  selectWidgetState,
  (widgetState: WidgetState) => widgetState.selectedPreviewWidgetConfig,
);

export const selectSelectedPlaceholderId = createSelector(
  selectWidgetState,
  (widgetState: WidgetState) => widgetState.selectedPlaceholderWidgetId,
);

export const selectFeedAlertsCount = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.alertsCount,
);

export const selectFeedFilters = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.filters,
);

export const selectFeedCurrentPage = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.page,
);

export const selectFeedItemsPerPage = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.itemsCount,
);

export const selectFeedEditMode = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.editMode,
);

export const selectFeedActiveFilterItems = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.activeFilterItems,
);

export const selectFeedSelectedFilterItems = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.selectedFilterItems,
);

export const selectFeedSearchQuery = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.feed?.view_properties?.quick_search_query,
);

export const selectAdvancedSearchOpen = createSelector(
  selectFeedState,
  (feedState: FeedState) =>
    feedState.feed?.view_properties?.show_advanced_quick_search,
);

export const selectLoadingAlgorithmPostIds = createSelector(
  selectFeedState,
  (feedState) => feedState.loadingAlgorithmPostIds,
);

export const selectAppliedAlgorithmPostIds = createSelector(
  selectFeedState,
  (feedState) => feedState.appliedAlgorithmPostIds,
);

export const selectFeedPostsViewMode = createSelector(
  selectFeedState,
  (feedState) => feedState.feed?.view_posts_mode,
);

export const selectFeed = createSelector(
  selectFeedState,
  (feedState: FeedState) => feedState.feed,
);

export const selectPeriod = createSelector(
  selectObjectState,
  (state: ObjectState) => state.period,
);

/** The picker preset behind the current period, null for a hand-picked or restored range (WAP-1763). */
export const selectPeriodPreset = createSelector(
  selectObjectState,
  (state: ObjectState) => state.periodPreset,
);

export const selectPeriodLocked = createSelector(
  selectObjectState,
  (state: ObjectState) => state.isPeriodLocked,
);

export const selectObjectIsExporting = createSelector(
  selectObjectState,
  (state: ObjectState) => state.isExporting,
);

export const selectPeriodsHistory = createSelector(
  selectObjectState,
  (state: ObjectState) => state.periodsHistory,
);

export const selectDeafaultPeriod = createSelector(
  selectObjectState,
  (state: ObjectState) => state.defaultPeriod,
);

export const selectIsDefaultPeriod = createSelector(
  selectObjectState,
  (state: ObjectState) => state.isDefaultPeriod,
);

export const selectExportSchedulesCount = createSelector(
  selectObjectState,
  (state: ObjectState) => state.exportSchedulesCount,
);

export const selectSelectedPrincipalsByIds = (ids: string[]) =>
  createSelector(selectPrincipalsState, (state: PrincipalState) => {
    const idSet = new Set(ids);
    const result: Principal[] = [];

    state.selectedPrincipals.forEach((principal, id) => {
      if (idSet.has(id)) {
        result.push(principal);
      }
    });

    return result;
  });

export const selectPricipalsSelectedMap = createSelector(
  selectPrincipalsState,
  (state: PrincipalState) => {
    return state.selectedPrincipals;
  },
);

export const selectPrincipalsCurrentCompanyId = createSelector(
  selectPrincipalsState,
  (state: PrincipalState) => state.currentCompanyId,
);
export const selectPrincipals = createSelector(
  selectPrincipalsState,
  (state: PrincipalState) => state.principals,
);

export const selectPrincipalsLoading = createSelector(
  selectPrincipalsState,
  (state: PrincipalState) => state.loading,
);

export const selectPrincipalsTotal = createSelector(
  selectPrincipalsState,
  (state: PrincipalState) => state.total,
);

export const selectHasShadow = createSelector(
  selectCoreState,
  (state: CoreState) => state.hasShadow,
);

export const selectCurrentObject = createSelector(
  selectObjectState,
  (state: ObjectState) => state.object,
);

export const selectCurrentObjectWorkspaceId = createSelector(
  selectObjectState,
  (state: ObjectState) => state.object?.workspace.id,
);

export const selectCurrentObjectEditMode = createSelector(
  selectObjectState,
  (state: ObjectState) => state.object?.['user_settings']?.edit_mode ?? true, // Default to true if not set
);

export const selectCurrentObjectCacheEnabled = createSelector(
  selectObjectState,
  (state: ObjectState) => state.object?.cache_enabled ?? true,
);

export const selectActiveWorkspaceId = createSelector(
  selectObjectState,
  (state: ObjectState) => state.activeWorkspaceId,
);

/**
 * The **bulk-delete** spinner for the widget area, not the widget list's loading
 * state - that comes from `injectObjectWidgetsQuery`.
 */
export const selectIsLoadingWidgets = createSelector(
  selectWidgetState,
  (state) => state.loadingWidgets,
);

export const selectFeedSettings = createSelector(selectFeedState, (state) => {
  let data: FeedSettings = {
    sort_direction: state.sortDir,
    post_time_mode: state.feed?.post_time_mode,
    post_time_field: state.feed?.post_time_field,
    order_field: state.sortField,
    items_per_page: state.itemsCount,
    post_display_thumbnail: state.feed?.post_display_thumbnail,
    time_shown: state.timeShown,
    view_properties: {
      widget_display_attached_pivots:
        state.feed?.view_properties.widget_display_attached_pivots,
      widget_display_attached_filters:
        state.feed?.view_properties.widget_display_attached_filters,
      widget_display_attached_data_sources:
        state.feed?.view_properties.widget_display_attached_data_sources,
      post_sentiment_algorithm_id:
        state.feed?.view_properties.post_sentiment_algorithm_id,
      show_advanced_quick_search:
        state.feed?.view_properties.show_advanced_quick_search,
      quick_search_query: state.feed?.view_properties.quick_search_query,
    },
  };

  return data;
});

export const selectObjectWidgetViewProperties = createSelector(
  selectObjectState,
  (state: ObjectState) => {
    return (
      state.object?.view_properties ?? {
        widget_display_attached_pivots: true,
        widget_display_attached_filters: true,
        widget_display_attached_data_sources: true,
        post_sentiment_algorithm_id: null,
      }
    );
  },
);

/// feed state selectors

export const objectClassObjectTypeIdsStatePathMap = {
  [NomObjectClass.FEED]: {
    [NomObjectTypes.ONLINE_FEED]: 'feeds',
    [NomObjectTypes.STORY_FEED]: 'feeds',
    [NomObjectTypes.COMBINED_FEED]: 'feeds',
    [NomObjectTypes.SOCIAL_FEED]: 'feeds',
    [NomObjectTypes.CLIPPING_FEED]: 'feeds',
    [NomObjectTypes.BROADCAST_FEED]: 'feeds',
    [NomObjectTypes.SEARCH_DIMENSION]: 'dimensions',
  },
  [NomObjectClass.WORKSPACE]: 'workspaces',
  [NomObjectClass.DASHBOARD]: 'dashboards',
  [NomObjectClass.USER]: 'users',
  [NomObjectClass.USER_GROUP]: 'groups',
};

export const getStatePathsFromObjectTypeIds = (
  objectClassIds: Array<number>,
  objectTypeIds: NomObjectTypes[],
): Array<keyof CoreState> => {
  const names = new Set<string>();
  objectClassIds.forEach((objectClassId) => {
    const objectTypes = objectClassObjectTypeIdsStatePathMap[objectClassId];
    if (typeof objectTypes === 'string') {
      names.add(objectTypes);
    } else if (typeof objectTypes === 'object') {
      objectTypeIds.forEach((typeId) => {
        const name = objectTypes[typeId];
        if (name) {
          names.add(name);
        }
      });
    }
  });

  return Array.from(names) as Array<keyof CoreState>;
};

export const selectSelectedPostsCount = createSelector(
  selectFeedState,
  (feedState) => feedState.feed?.count_selected_posts ?? 0,
);

/** Posts State */

export const selectPostState = (state: AppState) => state.post;

export const selectSelectedPostId = createSelector(
  selectPostState,
  (postState) => postState.selectedPostId,
);

export const selectSelectedState = (state: AppState) => state.selected;

export const selectSelectedObjects = createSelector(
  selectSelectedState,
  (selectedState) => selectedState.selectedObjects,
);

export const selectSelectedObjectsArray = createSelector(
  selectSelectedObjects,
  (selectedObjects) => Array.from(selectedObjects.values()),
);

export const selectSelectedObjectsCount = createSelector(
  selectSelectedObjects,
  (selectedObjects) => selectedObjects.size,
);

export const isObjectSelected = (objectId: string) =>
  createSelector(selectSelectedObjects, (selectedObjects) =>
    selectedObjects.has(objectId),
  );
export const selectUserAccessLevel = createSelector(
  selectUser,
  (user: User) => user?.access_level_id || UserAccessLevel.CLIENT_USER,
);

export const selectUserAccessLevelName = createSelector(
  selectUser,
  (user: User) => user?.user_access_level.name,
);

export const selectHasAccess = (requiredAccessLevel: UserAccessLevel) =>
  createSelector(
    selectUserAccessLevel,
    (userAccessLevel: number) => userAccessLevel >= requiredAccessLevel,
  );

export const selectUserAccessLevelGuest = selectHasAccess(
  UserAccessLevel.GUEST,
);
export const selectUserAccessLevelClientUser = selectHasAccess(
  UserAccessLevel.CLIENT_USER,
);
export const selectUserAccessLevelClientAdmin = selectHasAccess(
  UserAccessLevel.CLIENT_ADMIN,
);
export const selectUserAccessLevelAgencyUser = selectHasAccess(
  UserAccessLevel.AGENCY_USER,
);
export const selectUserAccessLevelAgencyAdmin = selectHasAccess(
  UserAccessLevel.AGENCY_ADMIN,
);
export const selectUserAccessLevelGlobalAdmin = selectHasAccess(
  UserAccessLevel.GLOBAL_ADMIN,
);
export const selectUserAccessLevelSystemAdmin = selectHasAccess(
  UserAccessLevel.SYSTEM_ADMIN,
);
export const selectUserAccessLevelPlatformAdmin = selectHasAccess(
  UserAccessLevel.PLATFORM_ADMIN,
);
export const selectUserAccessLevelPlatformOwner = selectHasAccess(
  UserAccessLevel.PLATFORM_OWNER,
);

export const selectUserWorkspaceContextSettings = createSelector(
  selectWorkspaceState,
  (state: WorkspaceState) => {
    return {
      contextKey: 'object_manager_workspace',
      contextValue: {
        filterString: state.filterString,
        sortingField: state.sortField,
        selectedOnly: false,
        showFilterTool: false,
        viewFields: [],
        filterFields: [],
        sortingDirection: state.sortDirection,
      },
      lifeCycleStatus: [NomObjectLifecycleStatus.ACTIVE],
    } as ObjectContextSettings;
  },
);

export const selectFormEntityState = (entity: string) =>
  createSelector(
    selectFormState,
    (state) =>
      state[entity] || {
        saving: false,
        success: false,
        error: null,
        object: null,
        isCreated: false,
      },
  );

export const selectMainMenuItems = createSelector(
  selectMainMenuState,
  selectUserAccessLevel,
  selectUser,
  (state, level, user) =>
    filterMenuByAccessLevel(state.menuItems, level, user?.feature_components),
);

export const selectMainMenuSecondaryItems = createSelector(
  selectMainMenuState,
  selectUserAccessLevel,
  selectUser,
  (state, level, user) =>
    filterMenuByAccessLevel(
      state.menuItemsSecondary,
      level,
      user?.feature_components,
    ),
);

export const selectFormEntitySaving = (entity: string) =>
  createSelector(selectFormEntityState(entity), (state) => state.saving);

export const selectFormEntitySuccess = (entity: string) =>
  createSelector(selectFormEntityState(entity), (state) => state.success);

export const selectFormEntityError = (entity: string) =>
  createSelector(selectFormEntityState(entity), (state) => state.error);

export const selectFormEntityObject = (entity: string) =>
  createSelector(selectFormEntityState(entity), (state) => state.object);

export const selectFormEntityIsCreated = (entity: string) =>
  createSelector(selectFormEntityState(entity), (state) => state.isCreated);

// * Dashboard Page selectors - Start
export const selectDashboardPages = createSelector(
  selectObjectState,
  (state: ObjectState) => {
    if (
      !state.object ||
      state.object.object_class_id !== NomObjectClass.DASHBOARD
    ) {
      return null;
    }
    // * Return original DashboardPage[] array for UI components (PageNavigator)
    return (state.object as Dashboard).pages || null;
  },
);

export const selectSelectedPageId = createSelector(
  selectObjectState,
  (state: ObjectState) => {
    if (
      !state.object ||
      state.object.object_class_id !== NomObjectClass.DASHBOARD
    ) {
      return null;
    }
    return (state.object as Dashboard).selected_page_id || null;
  },
);

export const selectCurrentPageState = createSelector(
  selectObjectState,
  selectSelectedPageId,
  (state: ObjectState, selectedPageId: number) => {
    if (
      !selectedPageId ||
      !state.object ||
      state.object.object_class_id !== NomObjectClass.DASHBOARD
    ) {
      return null;
    }

    const dashboard = state.object as Dashboard;
    const selectedPage = dashboard.pages.find(
      (page) => page.id === selectedPageId,
    );

    return selectedPage || null;
  },
);

// ! Isolated Period Selectors for Multi-Page Dashboard Support
// * These selectors provide complete isolation between Dashboard and Page periods

// * Factory selector for specific page period with inheritance logic
export const selectEffectivePeriodForPage = (pageId: number) =>
  createSelector(
    selectDashboardPages,
    selectPeriod,
    (pages, dashboardPeriod) => {
      if (!pages || !pageId) return dashboardPeriod;
      const page = pages.find((p) => p.id === pageId);
      const effectivePeriod = page?.period || dashboardPeriod;

      return effectivePeriod;
    },
  );

// * Selected page's effective period (page period, falling back to the object period)
export const selectEffectivePeriod = createSelector(
  selectSelectedPageId,
  selectDashboardPages,
  selectPeriod,
  (selectedPageId, pages, defaultPeriod) => {
    if (!selectedPageId || !pages) return defaultPeriod;
    const selectedPage = pages.find((p) => p.id === selectedPageId);

    return selectedPage?.period || defaultPeriod;
  },
);

// * Return Feed state active content filters
// * now uses effective period (selected Page's period OR Object period if page period is null)
export const getContentFilters = createSelector(
  selectFeedState,
  selectEffectivePeriod, // ! use Effective Period instead of Object period only
  (feedState, effectivePeriod) => {
    const activeFilterItems = feedState.activeFilterItems;
    const filters: Record<string, any> = {};
    const feedSearchQuery = feedState.feed?.view_properties.quick_search_query;

    activeFilterItems.forEach((filterItem: AnyFilterItem) => {
      const key = filterItem.property_uri;
      if (!key) return;

      const type = filterItem.type === 'exclude' ? 'exclude' : 'include';

      // make sure we always have { include: [], exclude: [] }
      if (!filters[key]) {
        filters[key] = { include: [], exclude: [] };
      }

      // 1) Normal value filters -> use raw_value
      if (isValueItem(filterItem) && filterItem.raw_value !== '') {
        filters[key][type].push(filterItem.raw_value);
        return;
      }

      // 2) Period filters -> encode the effective period as a filter value
      if (isPeriodItem(filterItem)) {
        // period should be the same as effectivePeriod
        const start = filterItem.time_start;
        const end = filterItem.time_end;

        // choose the shape your API expects:
        // a) object
        filters[key][type].push({ start, end });

        // OR, if your backend expects strings, you could do:
        // filters[key][type].push(`${start}-${end}`);

        return;
      }
    });

    if (feedSearchQuery && feedSearchQuery.length > 0) {
      const key = 'filter_queries';
      if (!filters[key]) {
        filters[key] = [];
      }
      filters[key].push(feedSearchQuery);
    }

    return {
      time_start: effectivePeriod.startTimestamp, // ! Effective period
      time_end: effectivePeriod.endTimestamp, // ! Effective period
      offset: feedState.offset,
      items_count: feedState.itemsCount,
      sort: feedState.sortDir,
      selected_only: feedState.selectedOnly,
      order: feedState.sortField,
      filters,
      // filter_query: feedSearchQuery,
    };
  },
);

// * Page-specific content filters for presentation mode
// * This selector factory creates content filters using a specific page's period
// * instead of the currently selected page's period
export const getContentFiltersForPage = (pageId: number | null) =>
  createSelector(
    selectFeedState,
    selectEffectivePeriodForPage(pageId), // Use existing page-specific period selector
    (feedState, effectivePeriod) => {
      let activeFilterItems = feedState.activeFilterItems;
      let filters: any = {};
      let feedSearchQuery = feedState.feed?.view_properties.quick_search_query;

      activeFilterItems.forEach((filterItem: AnyFilterItem) => {
        const key = filterItem.property_uri;
        if (!key) return;

        const type = filterItem.type === 'exclude' ? 'exclude' : 'include';

        // make sure we always have { include: [], exclude: [] }
        if (!filters[key]) {
          filters[key] = { include: [], exclude: [] };
        }

        // 1) Normal value filters -> use raw_value
        if (isValueItem(filterItem) && filterItem.raw_value !== '') {
          filters[key][type].push(filterItem.raw_value);
          return;
        }

        // 2) Period filters -> encode the effective period as a filter value
        if (isPeriodItem(filterItem)) {
          // period should be the same as effectivePeriod
          const start = filterItem.time_start;
          const end = filterItem.time_end;

          // choose the shape your API expects:
          // a) object
          filters[key][type].push({ start, end });

          // OR, if your backend expects strings, you could do:
          // filters[key][type].push(`${start}-${end}`);

          return;
        }
      });

      if (feedSearchQuery && feedSearchQuery.length > 0) {
        const key = 'filter_queries';
        filters[key] = filters[key] || [];
        // Push the id into the array
        filters[key].push(feedSearchQuery);
      }

      return {
        time_start: effectivePeriod.startTimestamp, // Page-specific period
        time_end: effectivePeriod.endTimestamp, // Page-specific period
        offset: feedState.offset,
        items_count: feedState.itemsCount,
        sort: feedState.sortDir,
        selected_only: feedState.selectedOnly,
        order: feedState.sortField,
        filters: filters,
        // This is used for filtering in results in Releated articles feature
        // filter_query: feedSearchQuery,
      };
    },
  );

// * Page Multi-Select selector
export const selectSelectedDashboardPageIds = createSelector(
  selectObjectState,
  (state) => state.selectedDashboardPageIds,
);
